/**
 * BranchHub realtime client — the WebSocket side of `routes/realtime.ts` and
 * `durable/branch-hub.ts`.
 *
 * PROTOCOL (mirrors the Durable Object exactly):
 *   1. `POST /v1/realtime/token { branchId, topics }` mints a 60-second HMAC
 *      connect ticket. The topics IN the ticket are the authorisation.
 *   2. Open `GET <api>/v1/realtime/connect?branchId=&token=&topics=&since=` as
 *      a WebSocket. The hub verifies the ticket BEFORE accepting the socket and
 *      answers with `{ op: 'welcome', seq, backlog? }`.
 *   3. Events arrive as `{ op: 'event', event: { seq, type, topic, … } }`.
 *      `seq` is the resume cursor: it is passed as `?since=` on reconnect so
 *      the hub replays the gap (bounded ring buffer; `truncated: true` means
 *      "refetch, do not trust the gap").
 *
 * DEGRADATION IS A FEATURE, NOT AN ERROR:
 *   - a 403 from the token mint (`branch.view` is not held by pure kitchen/bar
 *     roles) marks the channel `unavailable` and the screen keeps polling;
 *   - a socket that cannot open (the Worker does not yet expose the `/connect`
 *     upgrade proxy to the DO — see the gap list in lib/api.ts) reconnects with
 *     exponential backoff and, until it succeeds, the screen keeps polling;
 *   - every successful (re)connect fires `onCatchUp` so the consumer refetches
 *     once instead of trusting a possibly-truncated backlog.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { isApiError } from '@sonauto/api-client';
import { API_BASE_URL } from './api';
import type { PosApi } from './api';
import type { HubEventWire } from './types';

/** First retry after 1s, doubling to a 30s ceiling, ±20% jitter. */
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
const PING_INTERVAL_MS = 25_000;

export interface UseRealtimeInput {
  readonly api: PosApi;
  readonly branchId: string | null;
  /** e.g. `['tables','orders']` for a floor screen, `['kds:<id>']` for a KDS. */
  readonly topics: readonly string[];
  readonly enabled?: boolean;
  /** Called per event. Payloads are advisory — refetch, never patch caches. */
  readonly onEvent: (event: HubEventWire) => void;
  /** Called after every successful (re)connect. Do a full refetch here. */
  readonly onCatchUp?: () => void;
}

export interface RealtimeState {
  /** True while a socket is open AND welcomed. Poll slower, not never. */
  readonly connected: boolean;
  /**
   * True when the server REFUSED the ticket (403/400): this session may not
   * subscribe, so reconnecting is pointless. Polling is the design, not a bug.
   */
  readonly unavailable: boolean;
}

/** `/api` (dev proxy) or an absolute origin → the ws(s) URL of the connect path. */
export function hubConnectUrl(baseUrl: string): string {
  const absolute = /^https?:\/\//i.test(baseUrl)
    ? baseUrl
    : `${window.location.origin}${baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`}`;
  return `${absolute.replace(/^http/i, 'ws').replace(/\/+$/, '')}/v1/realtime/connect`;
}

interface WelcomeFrame {
  readonly op: 'welcome';
  readonly seq?: number;
  readonly backlog?: { events?: HubEventWire[]; truncated?: boolean };
}

/** Tolerant frame reader — a malformed frame is dropped, never a crash. */
function parseFrame(raw: unknown): { op: string; body: Record<string, unknown> } | null {
  if (typeof raw !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const body = parsed as Record<string, unknown>;
  return typeof body.op === 'string' ? { op: body.op, body } : null;
}

function isHubEvent(value: unknown): value is HubEventWire {
  if (value === null || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.seq === 'number' && typeof v.type === 'string' && typeof v.topic === 'string';
}

/**
 * Keep one BranchHub subscription alive for as long as the component lives.
 *
 * The consumer keeps its poll running (at a relaxed interval while
 * `connected`), so a silent socket failure can never freeze a screen — the
 * poll is the safety net the product spec demands of a POS.
 */
export function useRealtime(input: UseRealtimeInput): RealtimeState {
  const { api, branchId, enabled = true } = input;
  const [connected, setConnected] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // Callbacks live in refs so a re-render never tears the socket down.
  const onEventRef = useRef(input.onEvent);
  onEventRef.current = input.onEvent;
  const onCatchUpRef = useRef(input.onCatchUp);
  onCatchUpRef.current = input.onCatchUp;

  const topicsKey = input.topics.join(',');

  useEffect(() => {
    if (!enabled || branchId === null || topicsKey === '') {
      setConnected(false);
      setUnavailable(false);
      return undefined;
    }

    let alive = true;
    let ws: WebSocket | null = null;
    let retryTimer: number | undefined;
    let pingTimer: number | undefined;
    let attempts = 0;
    /** Last event seq seen — the `?since=` cursor for the next reconnect. */
    let lastSeq: number | null = null;
    const topics = topicsKey.split(',');

    const clearTimers = (): void => {
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      if (pingTimer !== undefined) window.clearInterval(pingTimer);
      retryTimer = undefined;
      pingTimer = undefined;
    };

    const scheduleRetry = (): void => {
      if (!alive) return;
      setConnected(false);
      attempts += 1;
      const base = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.min(attempts, 5));
      const jitter = base * 0.2 * (Math.random() * 2 - 1);
      retryTimer = window.setTimeout(() => void connect(), Math.round(base + jitter));
    };

    const connect = async (): Promise<void> => {
      if (!alive) return;

      // 1. Mint the 60s ticket. A permission refusal is FINAL for this session
      //    shape (until branch/topics change); a network failure is retried.
      let token: string;
      try {
        const minted = await api.realtime.token({ branchId, topics: [...topics] });
        token = minted.token;
      } catch (cause: unknown) {
        if (!alive) return;
        if (isApiError(cause) && (cause.status === 403 || cause.status === 400)) {
          setUnavailable(true);
          setConnected(false);
          return; // polling carries the screen from here on
        }
        scheduleRetry();
        return;
      }

      // 2. Dial the hub. The ticket travels in the query string because the
      //    browser WebSocket API cannot set headers; it is 60s-lived and
      //    single-purpose, which is what makes that acceptable.
      const url = new URL(hubConnectUrl(API_BASE_URL));
      url.searchParams.set('branchId', branchId);
      url.searchParams.set('token', token);
      url.searchParams.set('topics', topics.join(','));
      if (lastSeq !== null) url.searchParams.set('since', String(lastSeq));

      let socket: WebSocket;
      try {
        socket = new WebSocket(url.toString());
      } catch {
        scheduleRetry();
        return;
      }
      ws = socket;

      socket.onmessage = (message: MessageEvent<unknown>): void => {
        if (!alive) return;
        const frame = parseFrame(message.data);
        if (frame === null) return;

        if (frame.op === 'welcome') {
          attempts = 0;
          setConnected(true);
          setUnavailable(false);
          const welcome = frame.body as unknown as WelcomeFrame;
          if (typeof welcome.seq === 'number') lastSeq = welcome.seq;
          const backlog = welcome.backlog;
          for (const event of backlog?.events ?? []) {
            if (isHubEvent(event)) {
              lastSeq = event.seq;
              onEventRef.current(event);
            }
          }
          // A full refetch on every (re)connect: cheaper than reasoning about
          // whether the backlog was truncated, and always correct.
          onCatchUpRef.current?.();
          if (pingTimer !== undefined) window.clearInterval(pingTimer);
          pingTimer = window.setInterval(() => {
            try {
              socket.send(JSON.stringify({ op: 'ping' }));
            } catch {
              /* the close handler owns recovery */
            }
          }, PING_INTERVAL_MS);
          return;
        }

        if (frame.op === 'event' && isHubEvent(frame.body.event)) {
          const event = frame.body.event;
          lastSeq = event.seq;
          onEventRef.current(event);
        }
      };

      socket.onclose = (): void => {
        if (!alive) return;
        if (pingTimer !== undefined) window.clearInterval(pingTimer);
        pingTimer = undefined;
        scheduleRetry();
      };
      socket.onerror = (): void => {
        // onclose fires afterwards and owns the retry; closing here just makes
        // sure a half-open socket does not linger.
        try {
          socket.close();
        } catch {
          /* already closed */
        }
      };
    };

    void connect();

    return () => {
      alive = false;
      clearTimers();
      if (ws !== null) {
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        try {
          ws.close(1000, 'unmount');
        } catch {
          /* already closed */
        }
      }
      setConnected(false);
    };
  }, [api, branchId, enabled, topicsKey]);

  return useMemo<RealtimeState>(() => ({ connected, unavailable }), [connected, unavailable]);
}
